/**
 * Cashflow Forecast — Finance admin view.
 *
 * Two modes:
 *   1. Accounting Only — only actual e-conomic data (open invoices/payables).
 *   2. Accounting + Operating Expenses — overlays recurring/planned expenses
 *      from the existing Operating Expenses module.
 *
 * Primary view: monthly breakdown table (Working Capital / Operating Expenses
 * / Net Cash Movement / Ending Cash Position). The day-by-day timeline is
 * available below as a collapsible section.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt0 = (v) => `${Math.round(v || 0).toLocaleString()} EUR`;
const fmt2 = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signedFmt = (v) => {
    const n = v || 0;
    const sign = n > 0 ? '+' : (n < 0 ? '−' : '');
    return `${sign}${Math.abs(Math.round(n)).toLocaleString()}`;
};

export default function CashflowForecastPage() {
    const [windowDays, setWindowDays] = useState('90');
    const [includeOverdue, setIncludeOverdue] = useState(true);
    const [forecastMode, setForecastMode] = useState('with_opex'); // 'accounting_only' | 'with_opex'
    const [filterType, setFilterType] = useState('all');
    const [showTimeline, setShowTimeline] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    // Manual bank-balance override editor
    const [editingBank, setEditingBank] = useState(false);
    const [bankAmount, setBankAmount] = useState('');
    const [bankCurrency, setBankCurrency] = useState('DKK');
    const [bankName, setBankName] = useState('');
    const [bankNotes, setBankNotes] = useState('');
    const [savingBank, setSavingBank] = useState(false);

    const includeOpex = forecastMode === 'with_opex';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${API}/finance/cashflow-forecast`, {
                params: {
                    days: windowDays,
                    include_overdue: includeOverdue,
                    include_operating_expenses: includeOpex,
                },
            })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch(() => toast.error('Failed to load cashflow forecast'))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [windowDays, includeOverdue, includeOpex, refreshTick]);

    const openBankEditor = () => {
        const sc = data?.starting_cash;
        if (sc && sc.source === 'manual_override') {
            setBankAmount(String(sc.balance_native || ''));
            setBankCurrency(sc.currency || 'DKK');
            setBankName(sc.account_name && sc.account_name !== 'Manual override' ? sc.account_name : '');
            setBankNotes(sc.notes || '');
        } else {
            setBankAmount('');
            setBankCurrency(sc?.currency || 'DKK');
            setBankName('');
            setBankNotes('');
        }
        setEditingBank(true);
    };

    const saveBankBalance = async () => {
        const amt = parseFloat(bankAmount);
        if (!Number.isFinite(amt)) {
            toast.error('Angiv et gyldigt beløb');
            return;
        }
        setSavingBank(true);
        try {
            await axios.put(`${API}/finance/cashflow-starting-cash`, {
                amount_native: amt,
                currency: bankCurrency || 'DKK',
                bank_name: bankName || null,
                notes: bankNotes || null,
            });
            toast.success('Faktisk bankbeholdning opdateret');
            setEditingBank(false);
            setRefreshTick((n) => n + 1);
        } catch {
            toast.error('Kunne ikke gemme bankbeholdning');
        } finally {
            setSavingBank(false);
        }
    };

    const clearBankOverride = async () => {
        if (!window.confirm('Fjern manuel bankbeholdning og fald tilbage til ledger balance?')) return;
        setSavingBank(true);
        try {
            await axios.delete(`${API}/finance/cashflow-starting-cash`);
            toast.success('Manuel bankbeholdning fjernet');
            setEditingBank(false);
            setRefreshTick((n) => n + 1);
        } catch {
            toast.error('Kunne ikke fjerne bankbeholdning');
        } finally {
            setSavingBank(false);
        }
    };

    const timeline = (data?.timeline || []).filter((e) => filterType === 'all' || e.type === filterType);
    const monthly = data?.monthly_breakdown || [];

    return (
        <div className="max-w-7xl mx-auto px-6 py-6" data-testid="cashflow-forecast-page">
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-800">Cashflow Forecast</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Forventet kassebeholdning baseret på e-conomic + valgfri planlagte driftsudgifter.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Forecast mode segmented control */}
                    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="forecast-mode-toggle">
                        <button
                            onClick={() => setForecastMode('accounting_only')}
                            className={`px-3 py-1.5 transition ${forecastMode === 'accounting_only' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                            data-testid="mode-accounting-only"
                        >
                            Accounting Only
                        </button>
                        <button
                            onClick={() => setForecastMode('with_opex')}
                            className={`px-3 py-1.5 transition ${forecastMode === 'with_opex' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                            data-testid="mode-with-opex"
                        >
                            Accounting + Operating Expenses
                        </button>
                    </div>
                    <Select value={windowDays} onValueChange={setWindowDays}>
                        <SelectTrigger className="w-40" data-testid="range-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="30">Næste 30 dage</SelectItem>
                            <SelectItem value="60">Næste 60 dage</SelectItem>
                            <SelectItem value="90">Næste 3 måneder</SelectItem>
                            <SelectItem value="180">Næste 6 måneder</SelectItem>
                            <SelectItem value="365">Næste 12 måneder</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                        <Switch
                            id="include-overdue"
                            checked={includeOverdue}
                            onCheckedChange={setIncludeOverdue}
                            data-testid="toggle-overdue"
                        />
                        <Label htmlFor="include-overdue" className="text-xs text-slate-600 cursor-pointer">
                            Inkluder overdue OPEN
                        </Label>
                    </div>
                </div>
            </div>

            {loading || !data ? (
                <p className="text-sm text-slate-500">Indlæser cashflow data...</p>
            ) : (
                <>
                    {/* Starting cash card */}
                    <Card className="mb-5" data-testid="starting-cash-card">
                        <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                                    Faktisk bankbeholdning i dag
                                    {data.starting_cash.source === 'manual_override' && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-800" data-testid="starting-cash-source-badge">Manual</span>
                                    )}
                                    {data.starting_cash.source === 'economic_ledger' && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800" data-testid="starting-cash-source-badge">Ledger (fallback)</span>
                                    )}
                                </div>
                                <div className="text-3xl font-semibold text-slate-800 mt-1" data-testid="starting-cash-eur">
                                    {fmt0(data.starting_cash.balance_eur)}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    {fmt2(data.starting_cash.balance_native)} {data.starting_cash.currency}
                                    {data.starting_cash.account_name && ` · ${data.starting_cash.account_name}`}
                                </div>
                                {data.starting_cash.notes && (
                                    <div className="text-[11px] text-slate-500 mt-1 italic">{data.starting_cash.notes}</div>
                                )}
                                {/* Ledger drift indicator — only show when both values exist and differ meaningfully. */}
                                {data.starting_cash.source === 'manual_override' && data.starting_cash.ledger_balance_native != null && (() => {
                                    const drift = (data.starting_cash.balance_native || 0) - (data.starting_cash.ledger_balance_native || 0);
                                    if (Math.abs(drift) < 1) return null;
                                    const positive = drift > 0;
                                    return (
                                        <div className="text-[11px] text-slate-500 mt-2 border-t border-slate-100 pt-2">
                                            Ledger (konto {data.starting_cash.account_number}):{' '}
                                            <span className="font-medium text-slate-700">{Math.round(data.starting_cash.ledger_balance_native).toLocaleString('da-DK')} {data.starting_cash.currency}</span>
                                            {' · '}
                                            <span className={positive ? 'text-emerald-700' : 'text-amber-700'}>
                                                {positive ? '+' : ''}{Math.round(drift).toLocaleString('da-DK')} drift
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <Button variant="outline" size="sm" onClick={openBankEditor} data-testid="edit-bank-balance-btn" className="text-xs">
                                    <Pencil className="h-3 w-3 mr-1" />
                                    {data.starting_cash.source === 'manual_override' ? 'Opdater' : 'Angiv faktisk saldo'}
                                </Button>
                                <div className="text-right text-[11px] text-slate-500">
                                    Sidst opdateret: {new Date(data.starting_cash.last_synced_at).toLocaleString('da-DK')}
                                    {data.starting_cash.error && <div className="text-amber-600">{data.starting_cash.error}</div>}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Next VAT settlement card — real e-conomic snapshot when
                        available, otherwise estimated. Direction: outgoing =
                        we owe SKAT, incoming = refund coming back. */}
                    {data.next_vat_settlement && (() => {
                        const v = data.next_vat_settlement;
                        const isRefund = v.direction === 'incoming';
                        const sourceLabel = {
                            booked_in_economic: { text: 'Booked', cls: 'bg-emerald-100 text-emerald-800' },
                            estimated_zero: { text: 'No data yet', cls: 'bg-slate-100 text-slate-700' },
                            estimated_from_run_rate: { text: 'Estimated', cls: 'bg-amber-100 text-amber-800' },
                            not_configured: { text: 'Not configured', cls: 'bg-slate-100 text-slate-700' },
                            error: { text: 'Error', cls: 'bg-rose-100 text-rose-800' },
                        }[v.source] || { text: 'Estimated', cls: 'bg-amber-100 text-amber-800' };
                        const dueLabel = new Date(v.due_date).toLocaleDateString('da-DK', { day: '2-digit', month: 'short', year: 'numeric' });
                        return (
                            <Card className="mb-5" data-testid="next-vat-settlement-card">
                                <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                                            Next VAT Settlement
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${sourceLabel.cls}`} data-testid="vat-source-badge">
                                                {sourceLabel.text}
                                            </span>
                                        </div>
                                        <div className={`text-3xl font-semibold mt-1 ${isRefund ? 'text-emerald-700' : 'text-slate-800'}`} data-testid="next-vat-amount">
                                            {isRefund ? '+' : '−'} {fmt0(v.amount_eur)}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {Math.round(v.amount_native).toLocaleString('da-DK')} {v.currency}
                                            {' · '}Due <span className="font-medium text-slate-700" data-testid="next-vat-due-date">{dueLabel}</span>
                                            {' · '}<span className="capitalize">{v.frequency || 'quarterly'}</span> filer
                                        </div>
                                    </div>
                                    <div className="text-right text-xs text-slate-500 space-y-0.5">
                                        <div>Salgsmoms: <span className="font-medium text-slate-700">{Math.round(v.output_vat_native || 0).toLocaleString('da-DK')} {v.currency}</span></div>
                                        <div>Købsmoms: <span className="font-medium text-slate-700">− {Math.round(v.input_vat_native || 0).toLocaleString('da-DK')} {v.currency}</span></div>
                                        {(v.import_vat_native || 0) > 0 && (
                                            <div>Import moms: <span className="font-medium text-slate-700">− {Math.round(v.import_vat_native).toLocaleString('da-DK')} {v.currency}</span></div>
                                        )}
                                        <div className="pt-1 border-t border-slate-200 mt-1">
                                            Period {v.period_start} → {v.period_end}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {/* Forecast points */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                        {data.forecast_points.map((p) => (
                            <div
                                key={p.days_from_today}
                                className={`rounded-lg border p-4 ${p.projected_balance_eur < 0 ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-white'}`}
                                data-testid={`forecast-point-${p.days_from_today}`}
                            >
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">{p.label}</div>
                                <div className={`text-xl font-semibold mt-1 ${p.projected_balance_eur < 0 ? 'text-rose-700' : 'text-slate-800'}`}>
                                    {fmt0(p.projected_balance_eur)}
                                </div>
                                <div className="text-[10px] text-slate-400 mt-1">{p.date}</div>
                            </div>
                        ))}
                    </div>

                    {/* Monthly breakdown — primary view */}
                    <Card className="mb-5">
                        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                            <div>
                                <CardTitle className="text-sm font-semibold text-slate-700">Monthly Forecast</CardTitle>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Working Capital = customer receivables − supplier payables (actual e-conomic).{' '}
                                    Operating Expenses = recurring/planned from Operating Expenses module
                                    <span className="text-amber-700"> (forecast, ikke booket)</span>.
                                </p>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded ${includeOpex ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`} data-testid="mode-indicator">
                                {includeOpex ? 'Including operating expenses' : 'Accounting only'}
                            </span>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="monthly-breakdown-table">
                                    <thead>
                                        <tr className="text-left text-slate-500 border-b border-slate-200">
                                            <th className="py-2 pr-4 font-medium">Month</th>
                                            <th className="py-2 pr-4 font-medium text-right">Working Capital</th>
                                            <th className="py-2 pr-4 font-medium text-right">
                                                Operating Expenses
                                                <span className="ml-1 text-[9px] text-amber-700 align-top">forecast</span>
                                            </th>
                                            <th className="py-2 pr-4 font-medium text-right">VAT</th>
                                            <th className="py-2 pr-4 font-medium text-right">Net Cash Movement</th>
                                            <th className="py-2 pr-4 font-medium text-right">Ending Cash Position</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {monthly.length === 0 ? (
                                            <tr><td colSpan={6} className="py-4 text-center text-slate-400">Ingen måneder i vinduet.</td></tr>
                                        ) : monthly.map((r) => (
                                            <tr key={r.month} className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="py-2.5 pr-4 text-slate-700 font-medium" data-testid={`month-${r.month}`}>
                                                    {r.month_label}
                                                </td>
                                                <td className={`py-2.5 pr-4 text-right tabular-nums ${r.working_capital_eur < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                    {signedFmt(r.working_capital_eur)}
                                                </td>
                                                <td className="py-2.5 pr-4 text-right tabular-nums text-amber-700">
                                                    {r.operating_expenses_eur > 0 ? `−${Math.round(r.operating_expenses_eur).toLocaleString()}` : '0'}
                                                </td>
                                                <td className={`py-2.5 pr-4 text-right tabular-nums ${r.vat_eur > 0 ? 'text-rose-700' : r.vat_eur < 0 ? 'text-emerald-700' : 'text-slate-400'}`} data-testid={`vat-${r.month}`}>
                                                    {r.vat_eur > 0
                                                        ? `−${Math.round(r.vat_eur).toLocaleString()}`
                                                        : r.vat_eur < 0
                                                            ? `+${Math.round(Math.abs(r.vat_eur)).toLocaleString()}`
                                                            : '0'}
                                                </td>
                                                <td className={`py-2.5 pr-4 text-right tabular-nums font-medium ${r.net_cash_movement_eur < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                    {signedFmt(r.net_cash_movement_eur)}
                                                </td>
                                                <td className={`py-2.5 pr-4 text-right tabular-nums font-semibold ${r.ending_cash_position_eur < 0 ? 'text-rose-700' : 'text-slate-800'}`}>
                                                    {Math.round(r.ending_cash_position_eur).toLocaleString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Totals strip */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                        <Card><CardContent className="p-4">
                            <div className="text-xs text-slate-500">Forventet IND (vinduet)</div>
                            <div className="text-xl font-semibold text-emerald-700 mt-1">+ {fmt0(data.totals.expected_in_eur)}</div>
                        </CardContent></Card>
                        <Card><CardContent className="p-4">
                            <div className="text-xs text-slate-500">Forventet UD (vinduet){includeOpex && ' inkl. opex'}</div>
                            <div className="text-xl font-semibold text-rose-700 mt-1">- {fmt0(data.totals.expected_out_eur)}</div>
                        </CardContent></Card>
                        <Card><CardContent className="p-4">
                            <div className="text-xs text-slate-500">Netto-forskydning</div>
                            <div className={`text-xl font-semibold mt-1 ${data.totals.net_change_eur < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                {data.totals.net_change_eur > 0 ? '+' : ''}{fmt0(data.totals.net_change_eur)}
                            </div>
                        </CardContent></Card>
                    </div>

                    {(data.overdue_receivables.length > 0 || data.needs_review.length > 0) && (
                        <Card className="mb-5 border-amber-200 bg-amber-50">
                            <CardContent className="p-3 text-xs space-y-1">
                                {data.overdue_receivables.length > 0 && (
                                    <div>⚠ <strong>{data.overdue_receivables.length}</strong> overdue kundefakturaer
                                        ({fmt0(data.overdue_receivables.reduce((s, e) => s + (e.amount_eur || 0), 0))})
                                        — vises som forventet i dag.</div>
                                )}
                                {data.needs_review.length > 0 && (
                                    <div>⚠ <strong>{data.needs_review.length}</strong> fakturaer uden forfaldsdato — kræver gennemgang.</div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Collapsible day-by-day timeline */}
                    <Card>
                        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                            <CardTitle className="text-sm font-semibold text-slate-700">Day-by-day timeline</CardTitle>
                            <div className="flex items-center gap-2">
                                {showTimeline && (
                                    <Select value={filterType} onValueChange={setFilterType}>
                                        <SelectTrigger className="w-36 h-8 text-xs" data-testid="type-filter">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Alle</SelectItem>
                                            <SelectItem value="incoming">Kun ind</SelectItem>
                                            <SelectItem value="outgoing">Kun ud</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowTimeline((v) => !v)}
                                    data-testid="toggle-timeline-btn"
                                >
                                    {showTimeline ? 'Skjul' : 'Vis detaljeret timeline'}
                                </Button>
                            </div>
                        </CardHeader>
                        {showTimeline && (
                            <CardContent className="pt-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs" data-testid="cashflow-timeline">
                                        <thead>
                                            <tr className="text-left text-slate-500 border-b border-slate-200">
                                                <th className="py-2 pr-3 font-medium">Dato</th>
                                                <th className="py-2 pr-3 font-medium">Type</th>
                                                <th className="py-2 pr-3 font-medium">Part</th>
                                                <th className="py-2 pr-3 font-medium">Reference</th>
                                                <th className="py-2 pr-3 font-medium text-right">Beløb</th>
                                                <th className="py-2 pr-3 font-medium text-right">Δ (EUR)</th>
                                                <th className="py-2 pr-3 font-medium text-right">Saldo (EUR)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {timeline.length === 0 ? (
                                                <tr><td colSpan={7} className="py-4 text-center text-slate-400">Ingen events i vinduet.</td></tr>
                                            ) : timeline.map((e, i) => (
                                                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                                                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                                                        {e.effective_date}
                                                        {e.bucket === 'overdue' && <span className="ml-1 text-amber-600 text-[10px]">overdue</span>}
                                                        {e.kind === 'operating' && <span className="ml-1 text-amber-700 text-[10px]">forecast</span>}
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${e.type === 'incoming' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                            {e.type === 'incoming' ? 'IND' : 'UD'}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 pr-3 text-slate-700">{e.party}</td>
                                                    <td className="py-2 pr-3 text-slate-500 font-mono text-[11px]">{e.reference || '—'}</td>
                                                    <td className="py-2 pr-3 text-slate-600 text-right tabular-nums">{fmt2(e.amount_native)} {e.currency}</td>
                                                    <td className={`py-2 pr-3 text-right tabular-nums font-medium ${e.delta_eur < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                        {e.delta_eur > 0 ? '+' : ''}{fmt2(e.delta_eur)}
                                                    </td>
                                                    <td className="py-2 pr-3 text-right tabular-nums font-medium text-slate-800">{fmt2(e.running_balance_eur)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        )}
                    </Card>
                </>
            )}

            {/* Manual bank-balance override dialog. e-conomic REST doesn't
                expose the live bank-feed balance, so we let the admin paste
                the number from the bank app/portal. */}
            <Dialog open={editingBank} onOpenChange={setEditingBank}>
                <DialogContent className="sm:max-w-md" data-testid="bank-balance-dialog">
                    <DialogHeader>
                        <DialogTitle>Faktisk bankbeholdning</DialogTitle>
                        <DialogDescription>
                            Indtast den saldo, der reelt står i banken lige nu (fra din bank-app eller netbank).
                            Dette bruges som startpunkt for cashflow forecast — så tallet altid afspejler
                            virkeligheden i stedet for ledger-kontoen som halter efter uposterede bevægelser.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label htmlFor="bank-amount" className="text-xs">Saldo</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    id="bank-amount"
                                    type="number"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={bankAmount}
                                    onChange={(e) => setBankAmount(e.target.value)}
                                    placeholder="0,00"
                                    className="flex-1"
                                    data-testid="bank-amount-input"
                                />
                                <Select value={bankCurrency} onValueChange={setBankCurrency}>
                                    <SelectTrigger className="w-24" data-testid="bank-currency-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="GBP">GBP</SelectItem>
                                        <SelectItem value="SEK">SEK</SelectItem>
                                        <SelectItem value="NOK">NOK</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="bank-name" className="text-xs">Bank (valgfrit)</Label>
                            <Input
                                id="bank-name"
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                placeholder="Fx Danske Bank driftskonto"
                                data-testid="bank-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="bank-notes" className="text-xs">Note (valgfrit)</Label>
                            <Input
                                id="bank-notes"
                                value={bankNotes}
                                onChange={(e) => setBankNotes(e.target.value)}
                                placeholder="Fx pr. 7. juli 2026 kl. 09:00"
                                data-testid="bank-notes-input"
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex items-center justify-between gap-2">
                        {data?.starting_cash?.source === 'manual_override' ? (
                            <Button variant="ghost" size="sm" onClick={clearBankOverride} disabled={savingBank} className="text-rose-700 hover:text-rose-800" data-testid="clear-bank-balance-btn">
                                Fjern override
                            </Button>
                        ) : <span />}
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setEditingBank(false)} disabled={savingBank}>Annuller</Button>
                            <Button size="sm" onClick={saveBankBalance} disabled={savingBank || !bankAmount} data-testid="save-bank-balance-btn">
                                {savingBank ? 'Gemmer…' : 'Gem'}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
